import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { parse, stringify } from 'query-string';
import debounce from 'lodash.debounce';
import { Divider, InputAdornment, TextField } from '@material-ui/core';

// CONSTANTS
import { siteUrl, searchSections } from '@/constants/common';
import { LOCATIONS_SEARCH_PAGE } from '@/constants/locations';
import { ROUTE_SEARCH, ROUTE_PARTNER_SEARCH } from '@/constants/routes';

// UTILITY
import { getLink, routeEnhance } from '@/utility/routes';
import { getSearchParams } from '@/utility/common';
import { analyticsPageLoaded, analyticsLeftSearch, analyticsSearched } from '@/utility/analytics';
import { getOrgSchema } from '@/utility/orgSchemas/orgSchemas';

// STORE
import { SearchActions } from '@/store/actions';

// HOC
import Layout from '@/hoc/Layout';
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import ContentHero from '@/components/Heroes/ContentHero';
import SectionManager from '@/components/SectionManager';
import SectionSkeleton from '@/components/Skeletons/SectionSkeleton';

// HELP
import Anchors from './help/Anchors';
import NoResults from './help/NoResults';

// STYLES
import { Wrapper } from './styles';

const filterInitialParams = {
  search: '',
};

function getResultsCount(results) {
  let numberOfResults = 0;

  if (results.length) {
    results.forEach(item => {
      numberOfResults += item.data.length;
    });
  }

  return numberOfResults;
}

class Search extends Component {
  constructor(props) {
    super(props);

    this.state = {
      params: {
        ...filterInitialParams,
      },
    };
    this.debouncedSetQueryParams = debounce((data = {}) => {
      this.setQueryParams({ ...data });
    }, 1000);
    this.testRefs = [];
  }

  componentDidMount() {
    this.checkMethod();
  }

  componentDidUpdate(prevProps, prevState) {
    const { params: stateParams } = this.state;
    const {
      router: { location },
      isAuth,
      loading,
      data,
    } = this.props;
    const params = this.getSearchParams(location.search);
    const prevParams = this.getSearchParams(prevProps.router.location.search);
    const search = params.toString();
    const prevSearch = prevParams.toString();

    if (prevSearch !== search) {
      this.checkMethod();
    }

    if (prevProps.isAuth !== isAuth) {
      this.checkMethod();
    }

    if (JSON.stringify(stateParams) !== JSON.stringify(prevState.params)) {
      this.debouncedSetQueryParams(stateParams);
    }

    if (prevProps.loading !== loading && !loading && data.length) {
      const numberOfItemsDisplayed = getResultsCount(data);
      const firstItem = data[0].data[0];
      const first_item_name = firstItem.title || firstItem.name || firstItem.name;
      const first_item_id = firstItem.id || firstItem.channel_id;

      const properties = {
        query: stateParams.search,
        numberOfItemsDisplayed,
        first_item_type: data[0].type,
        first_item_name,
        first_item_id,
        search_location: LOCATIONS_SEARCH_PAGE,
      };

      analyticsSearched(properties);
    }
  }

  componentWillUnmount() {
    const { cleanSearchData } = this.props;

    cleanSearchData();
  }

  getList = (data = {}) => {
    const { params } = this.state;
    const { getSearchData } = this.props;
    const requestData = {
      ...params,
      ...data,
    };

    getSearchData(requestData);
  };

  getSearchParams = search => {
    const params = getSearchParams(search, false);
    params.delete('iframe');
    params.delete('partner');

    return params;
  };

  formatQueryParams = () => {
    const {
      router: { location },
    } = this.props;

    const params = this.getSearchParams(location.search);
    const search = params.toString();

    const queryParams = parse(search, {
      skipEmptyString: true,
      parseNumbers: true,
    });

    return queryParams;
  };

  setParams = () => {
    const queryParams = this.formatQueryParams();
    const newParams = {
      ...filterInitialParams,
      ...queryParams,
    };

    this.setState({ params: newParams }, () => this.getList(newParams));
  };

  setQueryParams = (newParams = {}) => {
    const { params: stateParams } = this.state;
    const {
      router: { navigate, location },
    } = this.props;

    const params = { ...stateParams, ...newParams };

    const search = stringify(params, {
      arrayFormat: 'comma',
      skipEmptyString: true,
    });

    const linkWithParams = routeEnhance(`${ROUTE_SEARCH}?${search}`);

    if (
      `${location.pathname}${location.search}` !==
      `${linkWithParams.pathname}${linkWithParams.search ? `?${linkWithParams.search}` : ''}`
    ) {
      navigate(linkWithParams, { replace: true });
    }
  };

  checkMethod = () => {
    const {
      router: { location },
    } = this.props;

    const params = this.getSearchParams(location.search);
    const search = params.toString();

    if (search) {
      this.setParams();
    } else {
      this.setQueryParams();
      this.getList();
    }
  };

  handleSearchChange = ({ target: { name, value } }) => this.handleChange(name, value);

  handleChange = (name, value) => {
    const { params } = this.state;
    const data = {
      [name]: value,
    };

    this.setState({ params: { ...params, ...data } });
  };

  handleSearchBlur = ({ target: { value } }) => {
    const { data } = this.props;
    const numberOfResults = getResultsCount(data);

    const properties = {
      query: value,
      numberOfResults,
      search_location: LOCATIONS_SEARCH_PAGE,
    };

    analyticsLeftSearch(properties);
  };

  render() {
    const { params } = this.state;
    const { t, isIframe, data, loading, partner, partnerLoading } = this.props;

    const isPartner = !!partner.slug;
    const title = params.search
      ? t('searchPage.title', { search: params.search })
      : t('searchPage.searchPlaceholder');

    const sections = data.map((section, index) => {
      const ref = React.createRef();
      this.testRefs[index] = ref;

      return (
        <div ref={this.testRefs[index]} key={index}>
          <SectionManager
            id={section.type}
            data={section}
            analyticsLocation={LOCATIONS_SEARCH_PAGE}
          />
          {data.length - 1 !== index && <Divider className="search__divider" />}
        </div>
      );
    });

    const anchors = data
      .map((section, index) => {
        const itemData = searchSections.find(item => item.type === section.type);

        if (itemData) itemData.ref = this.testRefs[index];

        return itemData || null;
      })
      .filter(i => i !== null);

    const seoData = {
      canonicalUrl: `${siteUrl}${ROUTE_SEARCH}`,
      ogUrl: isPartner
        ? `${siteUrl}${getLink(ROUTE_PARTNER_SEARCH, { partner: partner.slug })}`
        : `${siteUrl}${ROUTE_SEARCH}`,
      title: 'Search',
      description: 'You can find TV Shows, movies, apps, channels etc.',
    };

    return (
      <Layout>
        {!partnerLoading && (
          <Helmet
            onChangeClientState={analyticsPageLoaded}
            script={[getOrgSchema('searchResults', seoData)]}
            titleTemplate="%s | MyBundle"
          >
            <title>{seoData.title}</title>
            <link rel="canonical" href={seoData.canonicalUrl} />
            <meta property="og:url" content={seoData.ogUrl} />
            <meta property="og:description" content={seoData.description} />
            <meta name="description" content={seoData.description} />
          </Helmet>
        )}
        <Wrapper className="search">
          <ContentHero
            className="search__content-hero"
            title={title}
            hideBreadcrumbs={isIframe}
            breadcrumbsProps={{ lastBreadcrumbText: t('common.search') }}
          >
            <Anchors
              className="search__anchors"
              data={anchors}
              title={anchors.length ? `${t('common.jumpTo')}:` : ''}
              loading={loading}
            />
            <div className="filters">
              <TextField
                className="filters__search"
                variant="filled"
                name="search"
                value={params.search}
                onChange={this.handleSearchChange}
                onBlur={this.handleSearchBlur}
                placeholder={t('searchPage.searchPlaceholder')}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    this.debouncedSetQueryParams.cancel();
                    this.setQueryParams();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment className="filters__search__icon" position="start">
                      <i className="icon-search" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </ContentHero>
          <div className="search__container section section--grey">
            <div className="page-content">
              {loading ? <SectionSkeleton /> : sections}
              {!loading && !data.length && <NoResults search={params.search} />}
            </div>
          </div>
        </Wrapper>
      </Layout>
    );
  }
}

Search.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  isIframe: PropTypes.bool.isRequired,
  isAuth: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  getSearchData: PropTypes.func.isRequired,
  cleanSearchData: PropTypes.func.isRequired,
  partner: PropTypes.object.isRequired,
  partnerLoading: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ app, auth, search, partner }) => ({
  isIframe: app.isIframe,
  isAuth: auth.isAuth,
  data: search.data || [],
  loading: search.loadings.data,
  partner: partner.data,
  partnerLoading: partner.loadings.partner,
});

const mapDispatchToProps = dispatch => {
  return {
    getSearchData: params => {
      dispatch(SearchActions.getSearchData.action({ params }));
    },
    cleanSearchData: () => {
      dispatch(SearchActions.cleanSearchData.action());
    },
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation(),
  withRouter,
)(Search);
